/**
 * FutureX — on-chain forecasting markets, deployed on Base mainnet.
 *
 * Every address is overridable from .env so a redeploy (or a switch to USDX's
 * own tokens) is a config change, not a code change. Token *names* are never
 * hard-coded: the UI reads each token's `symbol()` from the chain, so whatever
 * the addresses below point at is what users see.
 */
import { base } from "viem/chains";

const env = (name, fallback) => process.env?.[name] || fallback;

export const FUTUREX_CHAIN = base;

/** Base path the FutureX console is mounted under (see App.js). */
export const FUTUREX_BASE = "/futurex";

/** PredictionMarket UUPS proxy — always talk to the proxy, never the implementation. */
export const MARKET_CONTRACT = env(
    "REACT_APP_FUTUREX_CONTRACT",
    "0xBDD8c73d13961453870BA5720686310D9D4E7513",
);

/**
 * WalletConnect project ID (free at https://cloud.reown.com). Optional: without it the wallet modal offers browser
 * wallets only (MetaMask, Coinbase, Rabby and other injected wallets); with it, mobile wallets via QR code too.
 */
export const WALLETCONNECT_PROJECT_ID = env("REACT_APP_WALLETCONNECT_PROJECT_ID", "");

export const RPC_URL = env("REACT_APP_FUTUREX_RPC_URL", "https://mainnet.base.org");

/** Stake token (DAI on Base). Must be whitelisted on the market before a poll can use it. */
export const STABLE_TOKEN = env(
    "REACT_APP_FUTUREX_STABLE_TOKEN",
    "0x50c5725949A6F0c72E6C4a641F24049A917DB0Cb",
);

/** USDX (18 decimals). Also stakeable. */
export const PAYOUT_TOKEN = env(
    "REACT_APP_FUTUREX_PAYOUT_TOKEN",
    "0xf38671eA6290b43F30f2b685e86CDD125B86e13a",
);

/** Decimals of the stake token / pool accounting, and of the payout token. DAI is 18; set REACT_APP_FUTUREX_STABLE_DECIMALS if the stake token changes. */
export const STABLE_DECIMALS = Number(env("REACT_APP_FUTUREX_STABLE_DECIMALS", 18));
export const PAYOUT_DECIMALS = 18;

/**
 * The only wallet allowed into the admin console (and "view as").
 * Every admin surface is gated on this address; contract calls still need the
 * wallet to be authorised on-chain.
 */
export const ADMIN_WALLET = env(
    "REACT_APP_FUTUREX_DEFAULT_ADMIN",
    "0xf69606205CFbA58350fdd5e55db9C11116AD15Cc",
);

export const LOOKUP_ADMIN = env("REACT_APP_FUTUREX_LOOKUP_ADMIN", ADMIN_WALLET);

export const DEFAULT_ADMIN_WALLETS = [ADMIN_WALLET?.trim()?.toLowerCase()];

/** True only on a testnet chain, where the mock tokens expose a public mint(). */
export const IS_TESTNET = FUTUREX_CHAIN?.testnet === true;

/** Off-chain service that queues externally sourced markets for listing. */
export const SYNCER_URL = env("REACT_APP_FUTUREX_SYNCER_URL", "http://localhost:8092");

/** Leaderboard indexer (leaderboard-backend) — referral + participant rankings. */
export const LEADERBOARD_URL = env("REACT_APP_FUTUREX_LEADERBOARD_URL", "http://localhost:4000");

/** Where users convert a payout back to a stablecoin — the USDX Stable DEX. */
export const SWAP_URL = "https://dex.usdx.live/";

/** Tokens a forecast can be staked in, in picker order. */
export const STAKE_TOKENS = [STABLE_TOKEN, PAYOUT_TOKEN];

export const EXPLORER_URL = "https://basescan.org";
